import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

// Create axios instance
const api = axios.create({
    baseURL: API_URL,
    headers: {
        'Content-Type': 'application/json'
    }
});

// Add auth token to requests
api.interceptors.request.use((config) => {
    const token = localStorage.getItem('fleet_token');
    if (token) {
        config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
});

// Handle auth errors
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            localStorage.removeItem('fleet_token');
            window.location.href = '/login';
        }
        // Normalize FastAPI validation errors (detail as array/object) to a string
        const detail = error.response?.data?.detail;
        if (detail && typeof detail !== 'string') {
            error.response.data.detail = Array.isArray(detail)
                ? detail.map((d) => d?.msg || JSON.stringify(d)).join(', ')
                : (detail?.msg || JSON.stringify(detail));
        }
        return Promise.reject(error);
    }
);

// Dashboard APIs
export const dashboardAPI = {
    getStats: () => api.get('/dashboard/stats'),
    getFuelTrends: () => api.get('/dashboard/fuel-trends'),
    getMaintenanceTrends: () => api.get('/dashboard/maintenance-trends'),
    getVehicleStatus: () => api.get('/dashboard/vehicle-status')
};

// Vehicle APIs
export const vehicleAPI = {
    getAll: () => api.get('/vehicles'),
    getById: (id) => api.get(`/vehicles/${id}`),
    create: (data) => api.post('/vehicles', data),
    update: (id, data) => api.put(`/vehicles/${id}`, data),
    delete: (id) => api.delete(`/vehicles/${id}`)
};

// Driver APIs
export const driverAPI = {
    getAll: () => api.get('/drivers'),
    getById: (id) => api.get(`/drivers/${id}`),
    create: (data) => api.post('/drivers', data),
    update: (id, data) => api.put(`/drivers/${id}`, data),
    delete: (id) => api.delete(`/drivers/${id}`)
};

// Maintenance APIs
export const maintenanceAPI = {
    getAll: (params) => api.get('/maintenance', { params }),
    getById: (id) => api.get(`/maintenance/${id}`),
    create: (data) => api.post('/maintenance', data),
    update: (id, data) => api.put(`/maintenance/${id}`, data),
    delete: (id) => api.delete(`/maintenance/${id}`)
};

// Fuel Log APIs
export const fuelLogAPI = {
    getAll: (params) => api.get('/fuel-logs', { params }),
    getById: (id) => api.get(`/fuel-logs/${id}`),
    create: (data) => api.post('/fuel-logs', data),
    update: (id, data) => api.put(`/fuel-logs/${id}`, data),
    delete: (id) => api.delete(`/fuel-logs/${id}`)
};

// Trip APIs
export const tripAPI = {
    getAll: (params) => api.get('/trips', { params }),
    getById: (id) => api.get(`/trips/${id}`),
    create: (data) => api.post('/trips', data),
    update: (id, data) => api.put(`/trips/${id}`, data),
    delete: (id) => api.delete(`/trips/${id}`)
};

// Inspection APIs
export const inspectionAPI = {
    getAll: (params) => api.get('/inspections', { params }),
    getById: (id) => api.get(`/inspections/${id}`),
    create: (data) => api.post('/inspections', data),
    uploadPhoto: (inspectionId, photoType, file) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post(`/inspections/${inspectionId}/photos/${photoType}`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        });
    },
    getPhotoUrl: (inspectionId, photoType) => {
        const token = localStorage.getItem('fleet_token');
        return `${API_URL}/inspections/${inspectionId}/photos/${photoType}?auth=${token}`;
    },
    getCheckoutPhotoUrl: (inspectionId, photoType) => {
        const token = localStorage.getItem('fleet_token');
        return `${API_URL}/inspections/${inspectionId}/checkout-photos/${photoType}?auth=${token}`;
    },
    updateStatus: (id, status) => api.put(`/inspections/${id}/status?status=${status}`),
    getMyToday: () => api.get('/inspections/today/mine'),
    // AI Comparison
    aiCompare: (payload) => api.post('/inspections/compare-ai', payload),
    getComparisons: (params) => api.get('/inspections/comparisons', { params })
};

// User APIs (Admin)
export const userAPI = {
    getAll: () => api.get('/users'),
    updateRole: (id, role) => api.put(`/users/${id}/role?role=${role}`),
    delete: (id) => api.delete(`/users/${id}`)
};

export default api;
